import React from 'react';

interface BrochureModalProps {
    showBrochureModal: boolean;
    setShowBrochureModal: (show: boolean) => void;
    brochureStep: 'info' | 'otp';
    setBrochureStep: (step: 'info' | 'otp') => void;
    brochureForm: any;
    setBrochureForm: React.Dispatch<React.SetStateAction<any>>;
    brochureSubmitting: boolean;
    handleBrochureOtpSend: (e: React.FormEvent) => void;
    handleBrochureVerify: (e: React.FormEvent) => void;
    brochureError: string;
    setBrochureError: (error: string) => void;
}

const BrochureModal: React.FC<BrochureModalProps> = ({
    showBrochureModal,
    setShowBrochureModal,
    brochureStep,
    setBrochureStep,
    brochureForm,
    setBrochureForm,
    brochureSubmitting,
    handleBrochureOtpSend,
    handleBrochureVerify,
    brochureError,
    setBrochureError
}) => {
    if (!showBrochureModal) return null;

    return (
        <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(5,20,75,0.7)',
            backdropFilter: 'blur(10px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}>
            <div className="modal-content border-gray-200 shadow-lg rounded-2xl overflow-hidden" style={{ maxWidth: '450px', backgroundColor: '#fff' }}>
                <div className="p-6 relative">
                    <button
                        onClick={() => { setShowBrochureModal(false); setBrochureStep('info'); setBrochureError(''); }}
                        className="btn-close absolute top-0 right-0 m-3 border-0 bg-transparent"
                        style={{ fontSize: '10px' }}
                    ></button>

                    <div className="text-center mb-6">
                        <div className="bg-[#05144b] bg-opacity-10 text-[#05144b] rounded-full inline-flex items-center justify-center mb-6" style={{ width: '60px', height: '60px' }}>
                            <i className="fa-solid fa-file-pdf text-3xl"></i>
                        </div>
                        <h4 className="font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">Download Brochure</h4>
                        <p className="text-gray-500 text-sm">Please verify your details to download the project brochure.</p>
                    </div>

                    {brochureError && (
                        <div className="alert alert-danger extra-text-sm py-2 border-gray-200 rounded-lg mb-6 text-center">
                            <i className="fa-solid fa-circle-exclamation mr-2"></i>{brochureError}
                        </div>
                    )}

                    {brochureStep === 'info' ? (
                        <form onSubmit={handleBrochureOtpSend}>
                            <div className="mb-6 text-left">
                                <label className="form-label extra-text-sm font-bold text-gray-900">Full Name</label>
                                <input
                                    type="text"
                                    className="form-control text-lg rounded-lg bg-gray-50 border-gray-200"
                                    placeholder="Enter your name"
                                    required
                                    value={brochureForm.name}
                                    onChange={e => setBrochureForm((prev: any) => ({ ...prev, name: e.target.value }))}
                                />
                            </div>
                            <div className="mb-6 text-left">
                                <label className="form-label extra-text-sm font-bold text-gray-900">Email Address (For Verification)</label>
                                <input
                                    type="email"
                                    className="form-control text-lg rounded-lg bg-gray-50 border-gray-200"
                                    placeholder="john@example.com"
                                    required
                                    value={brochureForm.email}
                                    onChange={e => setBrochureForm((prev: any) => ({ ...prev, email: e.target.value }))}
                                />
                            </div>
                            <div className="mb-6 text-left">
                                <label className="form-label extra-text-sm font-bold text-gray-900">Mobile Number</label>
                                <div className="input-group">
                                    <span className="input-group-text border-gray-200 bg-gray-50 rounded-start-3 text-gray-500 font-bold">+91</span>
                                    <input
                                        type="tel"
                                        className="form-control text-lg rounded-end-3 bg-gray-50 border-gray-200"
                                        placeholder="98765 43210"
                                        required
                                        pattern="[0-9]{10}"
                                        value={brochureForm.mobile}
                                        onChange={e => setBrochureForm((prev: any) => ({ ...prev, mobile: e.target.value }))}
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={brochureSubmitting}
                                className="inline-block text-center cursor-pointer transition-all bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] w-full py-3 font-black rounded-full mt-2 shadow-lg border-2 border-[#f1ae24]/30 text-[13px] md:text-[14px] active:scale-95"
                            >
                                {brochureSubmitting ? (
                                    <><span className="spinner-border spinner-border-sm mr-2"></span>Sending OTP...</>
                                ) : 'Send OTP via Email'}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleBrochureVerify}>
                            <div className="mb-6 text-center">
                                <p className="text-sm text-gray-500 mb-2">An OTP has been sent to <span className="font-bold text-gray-900">{brochureForm.email}</span></p>
                                <label className="form-label extra-text-sm font-bold text-gray-900 block mb-2">Enter 6-Digit OTP</label>
                                <input
                                    type="text"
                                    className="form-control text-center font-bold text-2xl rounded-lg bg-gray-50 border-gray-200"
                                    placeholder="000000"
                                    maxLength={6}
                                    required
                                    value={brochureForm.otp}
                                    onChange={e => setBrochureForm((prev: any) => ({ ...prev, otp: e.target.value }))}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={brochureSubmitting}
                                className="inline-block text-center cursor-pointer transition-all bg-emerald-500 text-white w-full py-3 font-black rounded-full shadow-lg border-0 text-[13px] md:text-[14px] active:scale-95"
                            >
                                {brochureSubmitting ? (
                                    <><span className="spinner-border spinner-border-sm mr-2"></span>Verifying...</>
                                ) : 'Verify & Download'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setBrochureStep('info')}
                                className="inline-block text-center cursor-pointer transition-all btn-link w-full text-gray-500 text-sm mt-6 text-decoration-none border-0 bg-transparent"
                            >
                                <i className="fa-solid fa-arrow-left mr-1"></i> Edit Details
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BrochureModal;
